import { LICENCE_BODIES, LICENCE_STATES, MAX_LICENCE_WARNING_DAYS } from '@wodi/domain';
import { z } from 'zod';

// The local practitioner profile (docs/plans/phase1-consultation.md §2.13, §3.3, §7.4, P13):
// Settings > Practitioners and the signed-in clinician's own licence state.

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-22');

export const licenceStatusSchema = z.object({
  state: z.enum(LICENCE_STATES),
  /** `YYYY-MM-DD`, the last valid day. */
  expiresOn: isoDay.nullable(),
  daysLeft: z.number().int().nullable(),
});
export type LicenceStatusView = z.infer<typeof licenceStatusSchema>;

export const practitionerCadreSchema = z.object({
  code: z.string(),
  display: z.string(),
  system: z.string(),
});

export const practitionerViewSchema = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  userStatus: z.enum(['active', 'disabled']),
  cadre: practitionerCadreSchema.nullable(),
  licenceBody: z.enum(LICENCE_BODIES).nullable(),
  licenceNumber: z.string().nullable(),
  licenceExpiresOn: isoDay.nullable(),
  licenceIssuedOn: isoDay.nullable(),
  source: z.enum(['manual', 'health_worker_registry']),
  status: z.enum(['active', 'inactive']),
  licence: licenceStatusSchema,
  /** The Health Worker Registry link, when there is one (SHA plan §4.2). */
  registry: z
    .object({
      check: z.enum([
        'matches',
        'licence_mismatch',
        'registry_expired',
        'licence_unknown',
        'not_found',
      ]),
      words: z.string(),
      licenceValidTo: isoDay.nullable(),
    })
    .nullable(),
});
export type PractitionerView = z.infer<typeof practitionerViewSchema>;

export const practitionerListSchema = z.object({
  /** "Expires soon" this many days ahead (P13). */
  licenceWarningDays: z.number().int(),
  items: z.array(practitionerViewSchema),
  /** Active users without a profile yet, for "Add practitioner". */
  candidates: z.array(z.object({ userId: z.uuid(), displayName: z.string() })),
  /** The provisional cadre list (value set `practitioner_cadre`), active entries only. */
  cadres: z.array(practitionerCadreSchema),
});
export type PractitionerListView = z.infer<typeof practitionerListSchema>;

export const savePractitionerRequestSchema = z.object({
  cadreCode: z.string().trim().min(1).max(80),
  licenceBody: z.enum(LICENCE_BODIES),
  licenceNumber: z.string().trim().min(1).max(40),
  licenceExpiresOn: isoDay.nullable(),
  /** The day the licence was issued (staff records plan §2.1). Left out: unchanged. */
  licenceIssuedOn: isoDay.nullable().optional(),
  status: z.enum(['active', 'inactive']).default('active'),
});
export type SavePractitionerRequest = z.input<typeof savePractitionerRequestSchema>;

export const practitionerSettingsRequestSchema = z.object({
  licenceWarningDays: z.number().int().min(0).max(MAX_LICENCE_WARNING_DAYS),
});

/** `GET /me/practitioner`: for print headers and warnings. */
export const myPractitionerSchema = z.object({
  hasProfile: z.boolean(),
  /** "Name, Cadre", as prints carry it. */
  printName: z.string(),
  /** "COC licence …", or null when no number is recorded. */
  licenceText: z.string().nullable(),
  licenceBody: z.enum(LICENCE_BODIES).nullable(),
  licence: licenceStatusSchema,
});
export type MyPractitionerView = z.infer<typeof myPractitionerSchema>;
